import React, { useState } from "react"
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Card,
  Typography,
  IconButton,
  styled,
  Tooltip,
  useTheme,
  CircularProgress,
} from "@mui/material"
import {
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Edit,
  Visibility,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import AddDistrictModal from "page-sections/data-table/dataTableV2/AddDistrictModal"
import AddChapterModal from "./AddChapterModal"

// Styled components with theme-aware colors
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "12px 16px",
  color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  "&.header": {
    fontWeight: 600,
    fontSize: "0.875rem",
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.mode === 'dark' 
      ? "rgba(255, 255, 255, 0.05)" 
      : "rgba(0, 0, 0, 0.05)",
  },
  "& > .MuiTableCell-root": {
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  },
}))

const RegionRow = styled(TableRow)(() => ({
  // backgroundColor: 'rgb(28, 26, 63)',  // Slightly lighter for region rows
  "& > .MuiTableCell-root": {
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  },
}))

const SearchTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.grey[800],
    "& fieldset": {
      borderColor: theme.palette.grey[700],
    },
    "&:hover fieldset": {
      borderColor: theme.palette.grey[600],
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiInputBase-input": {
    color: theme.palette.common.white,
  },
  "& .MuiInputAdornment-root": {
    color: theme.palette.grey[400],
  },
}))

const StatCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.grey[800],
  height: "100%",
}))

const ChaptersDataTable = ({ data, isLoading }) => {
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState("")
  const [openModal, setOpenModal] = useState(false)
  const [openChapterModal, setOpenChapterModal] = useState(false)
  const [selectedDistrict, setSelectedDistrict] = useState(null)
  const [editData, setEditData] = useState(null)
  const navigate = useNavigate()
  const theme = useTheme()

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const filteredData = data.filter((chapter) => chapter.chapter_name.toLowerCase().includes(searchQuery.toLowerCase()))

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {/* Table */}
      <TableContainer component={Paper} sx={{ 
        backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'common.white',
        minHeight: 200,
        position: 'relative'
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell className="header">Chapter Name</StyledTableCell>
              <StyledTableCell className="header">Member Count</StyledTableCell>
              <StyledTableCell className="header">Business Done</StyledTableCell>
              <StyledTableCell className="header">Referrals Pass</StyledTableCell>
              <StyledTableCell className="header">One to One's</StyledTableCell>
              
              <StyledTableCell className="header" align="center">
                Action
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 'none' }}>
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 'none' }}>
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <Typography color="text.secondary">
                      No chapters found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((chapter) => (
                <StyledTableRow key={chapter.id}>
                  <StyledTableCell>
                    <Box>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: theme.palette.mode === 'dark' ? "common.white" : "common.black",
                          fontSize: "0.875rem",
                        }}
                      >
                        {chapter.chapter_name}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: theme.palette.mode === 'dark' 
                            ? "rgba(255, 255, 255, 0.5)" 
                            : "rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        {chapter.description}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography sx={{ 
                      color: theme.palette.mode === 'dark' ? "common.white" : "common.black",
                      fontSize: "0.875rem",
                    }}>
                      {chapter.count.member_count} Members
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography sx={{ 
                      color: theme.palette.mode === 'dark' ? "common.white" : "common.black",
                      fontSize: "0.875rem",
                    }}>
                      ₹{chapter.thank_you_amount_sum.toLocaleString('en-IN')}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography sx={{ 
                      color: theme.palette.mode === 'dark' ? "common.white" : "common.black",
                      fontSize: "0.875rem",
                    }}>
                      {chapter.referral_count} Referrals
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography sx={{ 
                      color: theme.palette.mode === 'dark' ? "common.white" : "common.black",
                      fontSize: "0.875rem",
                    }}>
                      {chapter.one_to_one_count} Meetings
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Tooltip title="Edit Chapter" arrow placement="top">
                      <IconButton
                        size="small"
                        sx={{ 
                          color: theme.palette.mode === 'dark' ? "white" : "rgba(0, 0, 0, 0.7)",
                          mr: 1,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(0, 0, 0, 0.1)'
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedDistrict(chapter.id)
                          setEditData(chapter)
                          setOpenChapterModal(true)
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Members" arrow placement="top">
                      <IconButton
                        size="small"
                        sx={{ 
                          color: theme.palette.mode === 'dark' ? "white" : "rgba(0, 0, 0, 0.7)",
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(0, 0, 0, 0.1)'
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/dashboard/members', {
                            state: { 
                              chapterId: chapter.id,
                              chapterName: chapter.chapter_name 
                            }
                          });
                        }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>

        <AddChapterModal
          open={openChapterModal}
          edit={Boolean(editData)}
          data={editData}
          onClose={() => {
            setOpenChapterModal(false)
            setEditData(null)
          }}
        />
      </TableContainer>
    </Box>
  )
}

export default ChaptersDataTable

